import React, { useState, useEffect } from 'react';
import { MapPin, Clock, DollarSign, TrendingUp, BarChart3, AlertCircle, Loader, ArrowLeftRight, ChevronDown, ChevronUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useData } from '../contexts/DataContext';
import { getLanePricing } from '../services/backendApiService';
import { useTheme } from '../contexts/ThemeContext';
import { useActivityLogger } from '../hooks/useActivityLogger';
import MapPreview from './MapPreview';

const EQUIPMENT_TYPES = {
  'Flatbed': ['F', 'FSD', 'SD', 'CONG', 'SDL'],
  'Dry Van': ['V', 'VR', 'SV'],
  'Reefer': ['R', 'VR'],
  'Hot Shot': ['HS']
};

function LanePricing() {
  const { freightData, lanePricingData, loading: dataLoading, error: dataError, dataSource } = useData();
  const { isDarkMode } = useTheme();
  const { logLanePricing } = useActivityLogger();
  
  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem('lanePricingFormData');
    if (savedFormData) {
      try {
        return JSON.parse(savedFormData);
      } catch (error) {
        console.warn('Failed to parse saved form data:', error);
      }
    }
    return {
      origin: 'Columbus, OH',
      destination: 'Chicago, IL',
      equipment: 'Flatbed'
    };
  });
  
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [showSupportingLoads, setShowSupportingLoads] = useState(false);

  // Generate chart data from supporting loads
  const generateChartData = (supportingLoads) => {
    if (!supportingLoads || supportingLoads.length === 0) {
      setChartData([]);
      return;
    }

    // Create rate per mile bins
    const bins = [
      { min: 0, max: 2.00, range: '$0.00-$2.00', count: 0, color: '#fee2e2' },
      { min: 2.00, max: 2.25, range: '$2.00-$2.25', count: 0, color: '#fecaca' },
      { min: 2.25, max: 2.50, range: '$2.25-$2.50', count: 0, color: '#fca5a5' },
      { min: 2.50, max: 2.75, range: '$2.50-$2.75', count: 0, color: '#f87171' },
      { min: 2.75, max: 3.00, range: '$2.75-$3.00', count: 0, color: '#ef4444' },
      { min: 3.00, max: 3.25, range: '$3.00-$3.25', count: 0, color: '#dc2626' },
      { min: 3.25, max: 3.50, range: '$3.25-$3.50', count: 0, color: '#b91c1c' },
      { min: 3.50, max: 10.00, range: '$3.50+', count: 0, color: '#991b1b' }
    ];

    // Count loads in each bin
    supportingLoads.forEach(load => {
      const rate = parseFloat(load.RatePerMile) || 0;
      const bin = bins.find(b => rate >= b.min && rate < b.max);
      if (bin) {
        bin.count++;
      }
    });

    // Filter out empty bins and set chart data
    const chartData = bins.filter(bin => bin.count > 0);
    setChartData(chartData);
    console.log(`📊 Generated chart data with ${chartData.length} bins from ${supportingLoads.length} supporting loads`);
  };

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('lanePricingFormData', JSON.stringify(formData));
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwapLocations = () => {
    setFormData(prev => ({
      ...prev,
      origin: prev.destination,
      destination: prev.origin
    }));
  };

  const geocodeLocation = async (cityState) => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    
    if (!MAPBOX_API_KEY) {
      // Fallback to mock data
      console.warn('Mapbox API key not found, using mock coordinates');
      const mockCoords = {
        'Columbus, OH': { lat: 39.9612, lon: -82.9988 },
        'Chicago, IL': { lat: 41.8781, lon: -87.6298 },
        'San Diego, CA': { lat: 32.7157, lon: -117.1611 },
        'Denver, CO': { lat: 39.7392, lon: -104.9903 },
        'Atlanta, GA': { lat: 33.7490, lon: -84.3880 }
      };
      return mockCoords[cityState] || mockCoords['Columbus, OH'];
    }

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(cityState)}.json`;
      const params = new URLSearchParams({
        access_token: MAPBOX_API_KEY,
        limit: '1',
        country: 'US'
      });
      
      const response = await fetch(`${url}?${params}`);
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        const [lon, lat] = feature.center;
        return { lat, lon };
      } else {
        throw new Error(`Location not found: ${cityState}`);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      throw error;
    }
  };

  const getDrivingDetails = async (originCoords, destCoords) => {
    const MAPBOX_API_KEY = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    
    if (!MAPBOX_API_KEY) {
      // Fallback to mock data
      console.warn('Mapbox API key not found, using mock driving details');
      const distance = Math.sqrt(
        Math.pow(destCoords.lat - originCoords.lat, 2) + 
        Math.pow(destCoords.lon - originCoords.lon, 2)
      ) * 69; // Rough miles conversion
      
      const duration = distance / 55; // Assume 55 mph average
      
      return {
        distance_mi: Math.round(distance),
        duration_hrs: Math.round(duration * 10) / 10,
        duration_desc: `${Math.floor(duration)}h${Math.round((duration % 1) * 60)}m`
      };
    }

    try {
      const url = `https://api.mapbox.com/directions-matrix/v1/mapbox/driving/${originCoords.lon},${originCoords.lat};${destCoords.lon},${destCoords.lat}`;
      const params = new URLSearchParams({
        access_token: MAPBOX_API_KEY,
        annotations: 'distance,duration'
      });
      
      const response = await fetch(`${url}?${params}`);
      const data = await response.json();
      
      if (data.distances && data.distances.length > 0) {
        const distance = data.distances[0][1]; // Distance in meters
        const duration = data.durations[0][1]; // Duration in seconds
        
        return {
          distance_mi: Math.round(distance / 1609.34 * 10) / 10,
          duration_hrs: Math.round(duration / 3600 * 10) / 10,
          duration_desc: `${Math.floor(duration / 3600)}h${Math.round((duration % 3600) / 60)}m`
        };
      } else {
        throw new Error('Unable to calculate driving details');
      }
    } catch (error) {
      console.error('Driving details error:', error);
      // Fallback to mock calculation
      const distance = Math.sqrt(
        Math.pow(destCoords.lat - originCoords.lat, 2) + 
        Math.pow(destCoords.lon - originCoords.lon, 2)
      ) * 69;
      
      const duration = distance / 55;
      
      return {
        distance_mi: Math.round(distance),
        duration_hrs: Math.round(duration * 10) / 10,
        duration_desc: `${Math.floor(duration)}h${Math.round((duration % 1) * 60)}m`
      };
    }
  };

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371.0; // Earth's radius in km
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lon1Rad = (lon1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const lon2Rad = (lon2 * Math.PI) / 180;
    
    const dlon = lon2Rad - lon1Rad;
    const dlat = lat2Rad - lat1Rad;
    
    const a = Math.sin(dlat / 2) ** 2 + Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(dlon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c * 0.621371; // Convert km to miles
  };

  const haversineFilter = (data, oLat, oLon, dLat, dLon, equipmentTypes) => {
    const maxRadiusMi = 300;
    const stepMi = 20;
    const minSamples = 10;
    let currentRadiusMi = 10;
    
    while (currentRadiusMi <= maxRadiusMi) {
      const filteredData = data.filter(load => {
        // Check if load has required coordinates
        if (!load.oLat || !load.oLon || !load.dLat || !load.dLon) return false;
        
        // Calculate distances from input origin/destination to load origin/destination
        const originDistance = haversineDistance(oLat, oLon, load.oLat, load.oLon);
        const destDistance = haversineDistance(dLat, dLon, load.dLat, load.dLon);
        
        // Check if within radius and equipment type matches
        return (
          originDistance <= currentRadiusMi &&
          destDistance <= currentRadiusMi &&
          equipmentTypes.includes(load.Equipment) &&
          load.LoadType === 'Full'
        );
      });
      
      if (filteredData.length >= minSamples) {
        return { filteredData, usedRadius: currentRadiusMi };
      }
      
      currentRadiusMi += stepMi;
    }
    
    // Return max radius results if min sample not met
    const filteredData = data.filter(load => {
      if (!load.oLat || !load.oLon || !load.dLat || !load.dLon) return false;
      
      const originDistance = haversineDistance(oLat, oLon, load.oLat, load.oLon);
      const destDistance = haversineDistance(dLat, dLon, load.dLat, load.dLon);
      
      return (
        originDistance <= maxRadiusMi &&
        destDistance <= maxRadiusMi &&
        equipmentTypes.includes(load.Equipment) &&
        load.LoadType === 'Full'
      );
    });
    
    return { filteredData, usedRadius: maxRadiusMi };
  };

  const calculatePricingData = (filteredData, distance) => {
    if (filteredData.length === 0) {
      return {
        averageRPM: 0,
        medianRPM: 0,
        stdRPM: 0,
        numLoads: 0,
        priceRange: [0, 0],
        medianPrice: 0
      };
    }
    
    // Extract RatePerMile values
    const rpmValues = filteredData.map(load => load.RatePerMile).filter(rpm => rpm > 0);
    
    if (rpmValues.length === 0) {
      return {
        averageRPM: 0,
        medianRPM: 0,
        stdRPM: 0,
        numLoads: 0,
        priceRange: [0, 0],
        medianPrice: 0
      };
    }
    
    // Calculate statistics
    const averageRPM = rpmValues.reduce((sum, rpm) => sum + rpm, 0) / rpmValues.length;
    const sortedRPM = [...rpmValues].sort((a, b) => a - b);
    const medianRPM = sortedRPM[Math.floor(sortedRPM.length / 2)];
    
    // Calculate standard deviation
    const variance = rpmValues.reduce((sum, rpm) => sum + Math.pow(rpm - averageRPM, 2), 0) / rpmValues.length;
    const stdRPM = Math.sqrt(variance);
    
    // Filter out outliers (within 2 standard deviations)
    const filteredRPM = rpmValues.filter(rpm => 
      rpm >= (averageRPM - 2 * stdRPM) && 
      rpm <= (averageRPM + 2 * stdRPM)
    );
    
    // Calculate price range (25th to 75th percentile)
    const sortedFilteredRPM = [...filteredRPM].sort((a, b) => a - b);
    const q1Index = Math.floor(sortedFilteredRPM.length * 0.25);
    const q3Index = Math.floor(sortedFilteredRPM.length * 0.75);
    const q1RPM = sortedFilteredRPM[q1Index] || medianRPM;
    const q3RPM = sortedFilteredRPM[q3Index] || medianRPM;
    
    return {
      averageRPM,
      medianRPM,
      stdRPM,
      numLoads: filteredData.length,
      priceRange: [distance * q1RPM, distance * q3RPM],
      medianPrice: distance * medianRPM
    };
  };

  const generateMockPricingData = (distance) => {
    // Fallback for when no real data is available
    const baseRPM = 2.50 + (distance / 1000) * 0.5;
    const variation = 0.3;
    
    return {
      averageRPM: baseRPM + (Math.random() - 0.5) * variation,
      medianRPM: baseRPM + (Math.random() - 0.5) * variation * 0.8,
      stdRPM: 0.25 + Math.random() * 0.15,
      numLoads: Math.floor(Math.random() * 100) + 50,
      priceRange: [
        distance * (baseRPM * 0.75),
        distance * (baseRPM * 1.25)
      ],
      medianPrice: distance * baseRPM
    };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const startTime = Date.now();
    
    // Cycling loading messages
    const messages = [
      'Checking similar lanes...',
      'Calculating averages...',
      'Getting route distance...',
      'Analyzing market rates...',
      'Finalizing results...'
    ];
    
    let messageIndex = 0;
    setLoadingMessage(messages[0]);
    
    // Change message every 1.2 seconds, but stop at the last message
    const messageInterval = setInterval(() => {
      messageIndex++;
      if (messageIndex < messages.length) {
        setLoadingMessage(messages[messageIndex]);
      } else {
        // Stop cycling once we reach "Finalizing results..."
        clearInterval(messageInterval);
      }
    }, 1200);
    
    try {
      // Parse city and state
      const [originCity, originState] = formData.origin.split(', ');
      const [destCity, destState] = formData.destination.split(', ');
      
      if (!originState || !destState) {
        throw new Error('Please enter city and state in format: "City, ST"');
      }

      // Get coordinates
      const originCoords = await geocodeLocation(formData.origin);
      const destCoords = await geocodeLocation(formData.destination);
      
      // Get driving details
      const drivingDetails = await getDrivingDetails(originCoords, destCoords);
      
      // Try backend API first, fallback to local data processing
      let pricingData;
      let usedRadius = 0;
      
      try {
        console.log('🚛 Lane Pricing: Calling backend API for real-time data...');
        const backendData = await getLanePricing({
          originCity,
          originState,
          destinationCity: destCity,
          destinationState: destState,
          equipmentType: formData.equipment
        });
        
        console.log('✅ Backend API response:', backendData);
        
        // Use backend data if available
        if (backendData && backendData.sample_size > 0) {
          pricingData = {
            distance_miles: backendData.distance_miles || drivingDetails.distance_mi,
            average_rate: backendData.average_rate || 0,
            median_rate: backendData.median_rate || 0,
            sample_size: backendData.sample_size || 0,
            price_range: backendData.price_range || [0, 0],
            median_price: backendData.median_price || 0,
            used_radius_miles: backendData.used_radius_miles || 0,
            loads_data: backendData.loads_data || [],
            supporting_loads: backendData.supporting_loads || []
          };
          console.log('✅ Using backend data with', pricingData.sample_size, 'loads');
        } else {
          throw new Error('No backend data available');
        }
      } catch (backendError) {
        console.log('⚠️ Backend API failed, trying local data:', backendError.message);
        
        // Fallback to local freight data processing
        if (freightData && freightData.length > 0) {
          const equipmentTypes = EQUIPMENT_TYPES[formData.equipment];
          const { filteredData, usedRadius: radius } = haversineFilter(
            freightData,
            originCoords.lat,
            originCoords.lon,
            destCoords.lat,
            destCoords.lon,
            equipmentTypes
          );

          pricingData = calculatePricingData(filteredData, drivingDetails.distance_mi);
          pricingData.supporting_loads = filteredData; // Add supporting loads for chart
          usedRadius = radius;
          console.log('✅ Using local data with', pricingData.sample_size, 'loads');
        } else {
          // Final fallback to mock data
          console.log('⚠️ No local data available, using mock data');
          pricingData = generateMockPricingData(drivingDetails.distance_mi);
          pricingData.supporting_loads = []; // No supporting loads for mock data
        }
      }

      // Log lane pricing activity using centralized logger
      try {
        await logLanePricing({
          origin: formData.origin,
          destination: formData.destination,
          equipment: formData.equipment
        });
      } catch (logError) {
        console.warn('Failed to log lane pricing activity:', logError);
      }

      setResults({
        origin: { city: originCity, state: originState },
        destination: { city: destCity, state: destState },
        equipment: formData.equipment,
        distance: pricingData.distance_miles || drivingDetails.distance_mi,
        duration: drivingDetails.duration_hrs,
        durationDesc: drivingDetails.duration_desc,
        originCoords: pricingData.origin_coords || originCoords,
        destCoords: pricingData.dest_coords || destCoords,
        usedRadius: pricingData.used_radius_miles || 0,
        averageRPM: pricingData.average_rate,
        medianRPM: pricingData.median_rate,
        numLoads: pricingData.sample_size,
        priceRange: pricingData.price_range || [0, 0],
        medianPrice: pricingData.median_price || (pricingData.distance_miles * pricingData.median_rate),
        loadsData: pricingData.loads_data || [],
        supportingLoads: pricingData.supporting_loads || []
      });

      // Generate chart data from supporting loads
      generateChartData(pricingData.supporting_loads || []);
      
    } catch (err) {
      setError(err.message);
    } finally {
      clearInterval(messageInterval);
      setLoadingMessage('');
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Lane Pricing Analysis</h2>
        <p className="mt-2 text-gray-600 dark:text-gray-400">Get market-based pricing for freight lanes between cities</p>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col lg:flex-row lg:items-end gap-4 lg:gap-2">
            <div className="w-full lg:flex-1">
              <label htmlFor="origin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Origin City, State
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  id="origin"
                  name="origin"
                  value={formData.origin}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  placeholder="Columbus, OH"
                  required
                />
              </div>
            </div>

            <div className="flex-shrink-0 self-center lg:self-auto">
              <button
                type="button"
                onClick={handleSwapLocations}
                className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 group"
                title="Swap origin and destination"
              >
                <ArrowLeftRight className="w-5 h-5 text-gray-600 dark:text-gray-400 group-hover:text-gray-800 dark:group-hover:text-gray-200 transition-transform duration-200 group-hover:scale-110 rotate-90 lg:rotate-0" />
              </button>
            </div>

            <div className="w-full lg:flex-1">
              <label htmlFor="destination" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Destination City, State
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  id="destination"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  placeholder="Chicago, IL"
                  required
                />
              </div>
            </div>

            <div className="w-full lg:w-48">
              <label htmlFor="equipment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Equipment Type
              </label>
              <select
                id="equipment"
                name="equipment"
                value={formData.equipment}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                required
              >
                {Object.keys(EQUIPMENT_TYPES).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="w-full lg:w-auto lg:flex lg:items-end">
              <button
                type="submit"
                disabled={loading}
                className="w-full lg:w-auto btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? loadingMessage : 'Get Pricing Analysis'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="space-y-8">
          <hr className="border-gray-200" />
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Pricing Details */}
            <div>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Pricing Details</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Route</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {results.origin.city}, {results.origin.state} → {results.destination.city}, {results.destination.state}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">(Full Load)</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Total Distance</p>
                      <p className="font-medium text-gray-900 dark:text-white">{results.distance.toLocaleString()} miles</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Driving Time</p>
                      <p className="font-medium text-gray-900 dark:text-white">{results.durationDesc}</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <DollarSign className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      <p className="text-sm font-medium text-primary-800 dark:text-primary-300">Median Market Price</p>
                    </div>
                    <p className="text-2xl font-bold text-primary-900 dark:text-primary-100">
                      ${results.medianPrice ? results.medianPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                    </p>
                    <p className="text-sm text-primary-700 dark:text-primary-300">
                      (${results.medianRPM ? results.medianRPM.toFixed(2) : '0.00'} per mile)
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Price Range (25th-75th percentile)</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      ${results.priceRange && results.priceRange[0] ? results.priceRange[0].toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0'} - ${results.priceRange && results.priceRange[1] ? results.priceRange[1].toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0'}
                    </p>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Average RPM</p>
                      <p className="font-medium text-gray-900 dark:text-white">${results.averageRPM ? results.averageRPM.toFixed(2) : '0.00'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600 dark:text-gray-400">Sample Size</p>
                      <p className="font-medium text-gray-900 dark:text-white">{results.numLoads || 0} loads</p>
                    </div>
                  </div>

                  {results.usedRadius > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Radius Used: <span className="font-medium">{results.usedRadius} miles</span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Data source: {dataSource}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Map Preview */}
            <div>
              <MapPreview 
                origin={`${results.origin.city}, ${results.origin.state}`}
                destination={`${results.destination.city}, ${results.destination.state}`}
                distance={results.distance}
                duration={results.duration}
                durationDesc={results.durationDesc}
                originCoords={results.originCoords}
                destCoords={results.destCoords}
              />
            </div>
          </div>

          {/* Loads Data Table */}
          {results.loadsData && results.loadsData.length > 0 && (
            <div className="mt-8">
              <div className="bg-white rounded-xl shadow-soft p-6 border border-gray-200">
                <div className="flex items-center space-x-2 mb-6">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  <h3 className="text-xl font-semibold text-gray-900">Similar Loads Found ({results.loadsData.length})</h3>
                  <div className="ml-auto text-sm text-gray-500">
                    Within {results.usedRadius} mile radius • Data source: {dataSource}
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origin</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Destination</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equipment</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Rate/Mile</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Miles</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pickup Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Broker</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {results.loadsData.slice(0, 20).map((load, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {load.OriginCity}, {load.OriginState}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {load.DestinationCity}, {load.DestinationState}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {load.Equipment}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            ${load.RatePerMile?.toFixed(2) || '0.00'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            ${load.PaymentAmount?.toLocaleString() || '0'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {load.Mileage?.toLocaleString() || '0'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {load.PickupDate || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {load.BrokerName || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {results.loadsData.length > 20 && (
                  <div className="mt-4 text-sm text-gray-500 text-center">
                    Showing first 20 of {results.loadsData.length} similar loads found
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chart */}
          <div className="mt-8">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-6">
                <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Rate Distribution</h3>
              </div>
              
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#374151' : '#f0f0f0'} />
                    <XAxis 
                      dataKey="range" 
                      fontSize={12}
                      tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280' }}
                    />
                    <YAxis 
                      fontSize={12}
                      tick={{ fill: isDarkMode ? '#9ca3af' : '#6b7280' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                        border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        color: isDarkMode ? '#f9fafb' : '#111827'
                      }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="flex items-center justify-center space-x-8 mt-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded"></div>
                  <span className="text-gray-600 dark:text-gray-400">Average: ${results.averageRPM ? results.averageRPM.toFixed(2) : '0.00'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded"></div>
                  <span className="text-gray-600 dark:text-gray-400">Median: ${results.medianRPM ? results.medianRPM.toFixed(2) : '0.00'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Supporting Loads Data Expander */}
          {results.supportingLoads && results.supportingLoads.length > 0 && (
            <div className="mt-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowSupportingLoads(!showSupportingLoads)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Supporting Loads Data ({results.supportingLoads.length} loads)
                    </h3>
                  </div>
                  {showSupportingLoads ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  )}
                </button>

                {showSupportingLoads && (
                  <div className="px-4 pb-4">
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Load ID
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Origin
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Destination
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Equipment
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Rate/Mile
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Payment
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Miles
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Pickup Date
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Broker
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                            {results.supportingLoads.map((load, index) => (
                              <tr key={load.LoadID || index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.LoadID}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.OriginCity}, {load.OriginState}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.DestinationCity}, {load.DestinationState}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.Equipment}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  ${parseFloat(load.RatePerMile || 0).toFixed(2)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  ${parseFloat(load.PaymentAmount || 0).toFixed(0)}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.Mileage}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.PickupDate}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {load.BrokerName}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {results.supportingLoads.length > 10 && (
                        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                          Showing all {results.supportingLoads.length} supporting loads used for pricing calculation
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LanePricing;